import { useState, useEffect } from "react";
import axios from "axios";
import {
  TbCash, TbPlus, TbRefresh, TbCheck, TbX,
  TbAlertCircle, TbChevronDown, TbChevronUp,
  TbCalendar, TbCurrencyEuro, TbUser, TbReceipt,
  TbCircleCheck, TbHourglass, TbFilter
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");
const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const fmtPeriod = (from, to) =>
  `${new Date(from).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} → ${new Date(to).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;

function StatusBadge({ status }) {
  return status === "paid" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <TbCircleCheck size={12} /> Payé
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700">
      <TbHourglass size={12} /> En attente
    </span>
  );
}

function CreatePayoutForm({ owners, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    ownerId:  "",
    from:     "",
    to:       "",
    expenses: 0,
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePreview = async () => {
    if (!form.ownerId || !form.from || !form.to) return;
    setPreviewing(true);
    try {
      // On appelle createPayout mais sans sauvegarder — on simule via le même endpoint
      // En vrai tu peux créer un endpoint GET /payouts/preview, mais ici on calcule côté front
      const owner = owners.find(o => o._id === form.ownerId);
      setPreview({
        ownerName:      owner?.name,
        commissionRate: owner?.commissionRate,
        expenses:       parseFloat(form.expenses) || 0,
      });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex flex-col gap-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nouveau reversement</p>

      <div className="grid grid-cols-2 gap-3">
        {/* Propriétaire */}
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Propriétaire *</label>
          <select
            name="ownerId"
            value={form.ownerId}
            onChange={handleChange}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          >
            <option value="">— Sélectionner —</option>
            {owners.map(o => (
              <option key={o._id} value={o._id}>
                {o.name} ({o.commissionRate}% commission)
              </option>
            ))}
          </select>
        </div>

        {/* Période */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Du *</label>
          <input
            type="date"
            name="from"
            value={form.from}
            onChange={handleChange}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Au *</label>
          <input
            type="date"
            name="to"
            value={form.to}
            onChange={handleChange}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          />
        </div>

        {/* Dépenses */}
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Dépenses à déduire (€)</label>
          <input
            type="number"
            name="expenses"
            min="0"
            value={form.expenses}
            onChange={handleChange}
            placeholder="0"
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          />
          <p className="text-xs text-gray-400">Ménage, réparations, charges... à déduire du reversement.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-100 transition"
        >
          Annuler
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={loading || !form.ownerId || !form.from || !form.to}
          className="px-4 py-2 text-sm bg-[#3A9D23] text-white rounded-xl hover:bg-[#2d8019] transition disabled:opacity-60 flex items-center gap-1.5"
        >
          <TbCheck size={15} />
          {loading ? "Génération..." : "Générer le reversement"}
        </button>
      </div>
    </div>
  );
}

function PayoutCard({ payout, onMarkPaid, marking }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

      {/* Header */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Avatar owner */}
        <div className="w-10 h-10 rounded-full bg-[#eaf3de] text-[#3B6D11] flex items-center justify-center text-sm font-medium shrink-0">
          {payout.ownerId?.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
        </div>

        {/* Infos */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {payout.ownerId?.name || "—"}
            </p>
            <StatusBadge status={payout.status} />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {fmtPeriod(payout.period.from, payout.period.to)}
          </p>
        </div>

        {/* Montant net */}
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-gray-900">
            {payout.netAmount?.toLocaleString("fr-FR")} €
          </p>
          <p className="text-xs text-gray-400">{toKMF(payout.netAmount)} KMF</p>
        </div>

        {expanded ? <TbChevronUp size={15} className="text-gray-400 shrink-0" /> : <TbChevronDown size={15} className="text-gray-400 shrink-0" />}
      </div>

      {/* Détail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-3 flex flex-col gap-4">

          {/* Calcul détaillé */}
          <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Détail du calcul</p>
            {[
              { label: "Revenus bruts",        value: payout.grossRevenue,     color: "text-gray-900" },
              { label: `Commission agence`,     value: -payout.agencyCommission,color: "text-orange-500" },
              { label: "Dépenses déduites",     value: -payout.expenses,        color: "text-red-400",
                hide: !payout.expenses },
            ].filter(i => !i.hide).map((item, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className={`text-sm font-semibold ${item.color}`}>
                  {item.value < 0 ? "−" : ""}{Math.abs(item.value).toLocaleString("fr-FR")} €
                </p>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200 mt-1">
              <p className="text-xs font-semibold text-gray-700">Net à reverser</p>
              <div className="text-right">
                <p className="text-base font-bold text-[#3A9D23]">{payout.netAmount?.toLocaleString("fr-FR")} €</p>
                <p className="text-xs text-gray-400">{toKMF(payout.netAmount)} KMF</p>
              </div>
            </div>
          </div>

          {/* Méta */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: TbReceipt,       label: "Réservations incluses", value: `${payout.reservationIds?.length || 0} résa` },
              { icon: TbCalendar,      label: "Généré le",             value: fmtDate(payout.createdAt) },
              { icon: TbUser,          label: "Email propriétaire",    value: payout.ownerId?.email || "—" },
              { icon: TbCurrencyEuro,  label: "Statut",                value: payout.status === "paid" ? `Payé le ${fmtDate(payout.paidAt)}` : "En attente de virement" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={12} className="text-gray-400" />
                    <p className="text-xs text-gray-400">{item.label}</p>
                  </div>
                  <p className="text-xs font-medium text-gray-800">{item.value}</p>
                </div>
              );
            })}
          </div>

          {/* Action marquer payé */}
          {payout.status === "pending" && (
            <button
              onClick={() => onMarkPaid(payout._id)}
              disabled={marking === payout._id}
              className="w-full py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-100 text-sm font-medium rounded-xl hover:bg-emerald-100 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <TbCheck size={16} />
              {marking === payout._id ? "Enregistrement..." : "Marquer comme payé"}
            </button>
          )}

          {payout.status === "paid" && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <TbCircleCheck className="text-emerald-600 shrink-0" size={16} />
              <p className="text-xs text-emerald-700 font-medium">
                Virement effectué le {fmtDate(payout.paidAt)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgencyPayouts() {
  const [payouts, setPayouts]   = useState([]);
  const [owners, setOwners]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [marking, setMarking]   = useState(null);
  const [filterStatus, setFilterStatus]   = useState("all");
  const [filterOwner, setFilterOwner]     = useState("all");

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/payouts`, { withCredentials: true }),
      axios.get(`${API}/owners`,  { withCredentials: true }),
    ])
      .then(([payoutsRes, ownersRes]) => {
        setPayouts(payoutsRes.data);
        setOwners(ownersRes.data);
      })
      .catch(() => setError("Impossible de charger les reversements."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (form) => {
    setFormLoading(true);
    try {
      const res = await axios.post(`${API}/payouts`, {
        ...form,
        expenses: parseFloat(form.expenses) || 0,
      }, { withCredentials: true });
      setPayouts(p => [res.data, ...p]);
      setShowForm(false);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la création.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleMarkPaid = async (id) => {
    setMarking(id);
    try {
      const res = await axios.patch(`${API}/payouts/${id}/pay`, {}, { withCredentials: true });
      setPayouts(p => p.map(x => x._id === id ? res.data : x));
    } catch {
      alert("Erreur lors de la mise à jour.");
    } finally {
      setMarking(null);
    }
  };

  const filtered = payouts
    .filter(p => filterStatus === "all" ? true : p.status === filterStatus)
    .filter(p => filterOwner  === "all" ? true : p.ownerId?._id === filterOwner);

  // Stats
  const totalPending = payouts.filter(p => p.status === "pending").reduce((s, p) => s + p.netAmount, 0);
  const totalPaid    = payouts.filter(p => p.status === "paid").reduce((s, p) => s + p.netAmount, 0);

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Reversements</h1>
          <p className="text-sm text-gray-400 mt-0.5">Suivi des virements aux propriétaires</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} className="w-9 h-9 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400">
            <TbRefresh size={16} />
          </button>
          <button
            onClick={() => setShowForm(s => !s)}
            className="flex items-center gap-2 px-4 py-2 bg-[#3A9D23] text-white text-sm font-medium rounded-xl hover:bg-[#2d8019] transition shadow-sm"
          >
            {showForm ? <TbX size={16} /> : <TbPlus size={16} />}
            {showForm ? "Annuler" : "Nouveau reversement"}
          </button>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total reversements", value: payouts.length,                                  color: "text-gray-900"    },
          { label: "En attente",         value: payouts.filter(p => p.status === "pending").length, color: "text-orange-500" },
          { label: "Montant en attente", value: `${totalPending.toLocaleString("fr-FR")} €`,    color: "text-orange-500" },
          { label: "Total reversé",      value: `${totalPaid.toLocaleString("fr-FR")} €`,       color: "text-emerald-600" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-400 mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── FORM CRÉATION ── */}
      {showForm && (
        <CreatePayoutForm
          owners={owners}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* ── FILTRES ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <TbFilter size={15} className="text-gray-400 shrink-0" />

        {/* Statut */}
        <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1">
          {[
            { key: "all",     label: "Tous" },
            { key: "pending", label: "En attente" },
            { key: "paid",    label: "Payés" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterStatus === f.key
                  ? "bg-[#3A9D23] text-white"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Propriétaire */}
        <select
          value={filterOwner}
          onChange={e => setFilterOwner(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] transition"
        >
          <option value="all">Tous les propriétaires</option>
          {owners.map(o => (
            <option key={o._id} value={o._id}>{o.name}</option>
          ))}
        </select>
      </div>

      {/* ── LISTE ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-gray-400">Chargement...</p>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <TbAlertCircle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
            <TbCash className="text-gray-400" size={24} />
          </div>
          <p className="text-sm font-medium text-gray-500">Aucun reversement trouvé</p>
          {payouts.length === 0 && (
            <button
              onClick={() => setShowForm(true)}
              className="text-xs text-[#3A9D23] hover:underline"
            >
              Générer votre premier reversement →
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map(payout => (
            <PayoutCard
              key={payout._id}
              payout={payout}
              onMarkPaid={handleMarkPaid}
              marking={marking}
            />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          {filtered.length} reversement{filtered.length > 1 ? "s" : ""}
          {" · "}Net total : {filtered.reduce((s, p) => s + p.netAmount, 0).toLocaleString("fr-FR")} €
        </p>
      )}
    </div>
  );
}